import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SearchResultItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { parseErrorMessage } from '../../core/api/api.context';
import {
  ALL_SEARCH_TYPES,
  DEFAULT_SEARCH_TYPES,
  isDeliverySignatureTerm,
  MIN_TERM_LENGTH,
  RESULTS_STALE_MS,
  SearchSubmission,
  useSearchQuery
} from '../../core/queries/search.queries';
import { SearchDrawer } from '../../ui/components/pure/search/search-drawer.component';
import { getResultPath } from '../../ui/components/pure/search/search-navigation';

export interface SearchContextValue {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

export const SearchContext = createContext<SearchContextValue>({} as SearchContextValue);

export const useSearchContext = () => {
  const { isOpen, onToggle } = useContext(SearchContext);
  return { isOpen, onToggle };
};

export function SearchContextProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return <SearchContext.Provider value={{ isOpen, onToggle: setIsOpen }}>{children}</SearchContext.Provider>;
}

export function SearchDrawerContainer({ params }: { params?: { projectID?: string; shortcode?: string } }) {
  const { isOpen, onToggle } = useSearchContext();
  const history = useHistory();

  const [term, setTerm] = useState('');
  const [includeDeliveries, setIncludeDeliveries] = useState(false);
  const [submission, setSubmission] = useState<SearchSubmission | undefined>(undefined);

  // When the last search ran, and whether the drawer was open on the previous render. Refs (not state)
  // so reading/updating them doesn't retrigger the reset effect — it must fire only on the open edge.
  const lastSearchedAtRef = useRef<number | undefined>(undefined);
  const wasOpenRef = useRef(isOpen);

  const query = useSearchQuery(submission);

  // On reopening the drawer, clear the inputs so a stale term/checkbox can't silently ride into the next search.
  // Recent results are kept — with the input empty, `SearchContent` labels them "Results for …" — so an accidental
  // close/reopen doesn't lose them; past the stale window it's a full wipe.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened) {
      return;
    }
    setTerm('');
    setIncludeDeliveries(false);
    const age = lastSearchedAtRef.current === undefined ? Infinity : Date.now() - lastSearchedAtRef.current;
    if (age >= RESULTS_STALE_MS) {
      setSubmission(undefined);
      lastSearchedAtRef.current = undefined;
    }
  }, [isOpen]);

  const handleTermChange = (next: string) => {
    // First keystroke into an empty box while kept results are on screen (the reopened "Results for …"
    // state): the user is starting a fresh search, so drop the stale results and their label now.
    if (submission && !term.trim() && next.trim()) {
      setSubmission(undefined);
      lastSearchedAtRef.current = undefined;
    }
    // A "GF-*" delivery signature implies the user wants deliveries searched. Auto-enable on the
    // rising edge (when the term first matches) so a manual uncheck afterwards still sticks.
    if (isDeliverySignatureTerm(next) && !isDeliverySignatureTerm(term)) {
      setIncludeDeliveries(true);
    }
    setTerm(next);
  };

  const handleSubmit = () => {
    const trimmed = term.trim();
    if (trimmed.length < MIN_TERM_LENGTH) {
      return;
    }
    lastSearchedAtRef.current = Date.now();
    setSubmission({
      term: trimmed,
      types: includeDeliveries ? ALL_SEARCH_TYPES : DEFAULT_SEARCH_TYPES
    });
  };

  const handleResultClick = (item: SearchResultItemDto) => {
    const path = getResultPath(item, params?.projectID);
    if (!path) {
      return;
    }
    onToggle(false);
    history.push(path);
  };

  const errorMessage = query.isError ? parseErrorMessage((query.error as RestError)?.message) : undefined;

  return (
    <SearchDrawer
      isOpen={isOpen}
      onToggle={onToggle}
      term={term}
      onTermChange={handleTermChange}
      includeDeliveries={includeDeliveries}
      onIncludeDeliveriesChange={setIncludeDeliveries}
      onSubmit={handleSubmit}
      isLoading={query.isFetching}
      hasSearched={!!submission}
      searchedTerm={submission?.term}
      results={query.data?.results ?? []}
      errorMessage={errorMessage}
      currentProjectID={params?.projectID}
      onResultClick={handleResultClick}
    />
  );
}
